import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { UserService } from '@/api/services/UserService';
import type { Routemize_DTOs_UserImageDTO } from '@/api/models/Routemize_DTOs_UserImageDTO';
import { useAuth } from './AuthContext';
import { AppConfigContext } from './AppConfigContext';

type ImageSize = 'full' | 'medium' | 'thumbnail' | 'thumb' | 'icon';

interface ProfilePictureContextType {
  images: Record<ImageSize, string | null>;
  isLoading: boolean;
  refreshProfilePicture: () => Promise<void>;
  getImageUrl: (size: ImageSize) => string | null;
}

const ProfilePictureContext = createContext<ProfilePictureContextType | undefined>(undefined);

export const useProfilePicture = () => {
  const context = useContext(ProfilePictureContext);
  if (!context) {
    throw new Error('useProfilePicture must be used within ProfilePictureProvider');
  }
  return context;
};

/**
 * Parse the image response and create a map of size -> URL
 * Tries to match filenames with size suffixes (e.g., _full.png, _thumb.png, _icon.png)
 * Falls back to using the first available image for all sizes if no matches found
 */
function parseImagesBySize(images: Routemize_DTOs_UserImageDTO[]): Record<ImageSize, string | null> {
  const result: Record<ImageSize, string | null> = {
    full: null,
    medium: null,
    thumbnail: null,
    thumb: null,
    icon: null,
  };

  // Try to match by filename suffix
  for (const img of images) {
    const fileName = img.fileName?.toLowerCase() ?? '';
    const url = img.url ?? null;

    if (fileName.includes('_full')) {
      result.full = url;
    } else if (fileName.includes('_medium')) {
      result.medium = url;
    } else if (fileName.includes('_thumbnail')) {
      result.thumbnail = url;
    } else if (fileName.includes('_thumb')) {
      result.thumb = url;
    } else if (fileName.includes('_icon')) {
      result.icon = url;
    }
  }

  // Fill in any missing sizes using the best available image.
  // Priority order: full > medium > thumbnail > thumb > icon, then reverse.
  // This ensures that if only "_full" is returned by the backend, all sizes
  // (including icon/thumb used by the navbar) resolve to that URL.
  const fallback = result.full ?? result.medium ?? result.thumbnail ?? result.thumb ?? result.icon
    ?? (images.length > 0 ? (images[0].url ?? null) : null);

  if (fallback) {
    for (const key of Object.keys(result) as ImageSize[]) {
      if (!result[key]) {
        result[key] = fallback;
      }
    }
  }

  return result;
}

/**
 * Parse profile photos from app config extraProperties
 * Converts the config dictionary to the same Record<ImageSize, string | null> format
 */
function parseConfigPhotos(profilePhotos: Record<string, string>): Record<ImageSize, string | null> {
  const result: Record<ImageSize, string | null> = {
    full: null,
    medium: null,
    thumbnail: null,
    thumb: null,
    icon: null,
  };

  // Map config keys to our ImageSize type
  if (profilePhotos.full) result.full = profilePhotos.full;
  if (profilePhotos.medium) result.medium = profilePhotos.medium;
  if (profilePhotos.thumbnail) result.thumbnail = profilePhotos.thumbnail;

  // Fill missing sizes using fallback logic (same pattern as parseImagesBySize)
  const fallback = result.full ?? result.medium ?? result.thumbnail ?? null;
  if (fallback) {
    for (const key of Object.keys(result) as ImageSize[]) {
      if (!result[key]) {
        result[key] = fallback;
      }
    }
  }

  return result;
}

export const ProfilePictureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const appConfig = useContext(AppConfigContext);
  const config = appConfig?.config ?? null;
  const [images, setImages] = useState<Record<ImageSize, string | null>>({
    full: null,
    medium: null,
    thumbnail: null,
    thumb: null,
    icon: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const loadProfilePicture = useCallback(async (userId: string, bustCache = false) => {
    try {
      setIsLoading(true);
      const imageList = await UserService.getApiAppUserGetImage(userId);
      if (imageList && imageList.length > 0) {
        const parsed = parseImagesBySize(imageList);
        // When busting cache (e.g. after upload), append a timestamp to each URL
        // so the browser fetches the fresh image from CDN instead of using its cache
        if (bustCache) {
          const ts = Date.now();
          for (const key of Object.keys(parsed) as ImageSize[]) {
            if (parsed[key]) {
              const sep = parsed[key]!.includes('?') ? '&' : '?';
              parsed[key] = `${parsed[key]}${sep}t=${ts}`;
            }
          }
        }
        setImages(parsed);
      } else {
        // No images found, reset to null
        setImages({
          full: null,
          medium: null,
          thumbnail: null,
          thumb: null,
          icon: null,
        });
      }
    } catch (err) {
      console.error('Error loading profile picture:', err);
      // Don't show error for missing profile picture, just keep nulls
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load profile picture when user authenticates
  // First try to source from app config, fallback to API if config data unavailable
  useEffect(() => {
    if (!isAuthenticated || !user?.profile?.sub) {
      // Reset images when user logs out
      setImages({
        full: null,
        medium: null,
        thumbnail: null,
        thumb: null,
        icon: null,
      });
      return;
    }

    // Check if config has profile photos
    const profilePhotos = config?.extraProperties?.profilePhotos as Record<string, string> | undefined;

    if (profilePhotos && Object.keys(profilePhotos).length > 0) {
      // Config data available - use it (no API call needed)
      const parsed = parseConfigPhotos(profilePhotos);
      setImages(parsed);
    } else {
      // Config data not available - fallback to API call
      loadProfilePicture(user.profile.sub);
    }
  }, [isAuthenticated, user?.profile?.sub, config?.extraProperties?.profilePhotos, loadProfilePicture]);

  const refreshProfilePicture = useCallback(async () => {
    if (user?.profile?.sub) {
      await loadProfilePicture(user.profile.sub, true);
    }
  }, [user?.profile?.sub, loadProfilePicture]);

  const getImageUrl = useCallback((size: ImageSize): string | null => {
    return images[size];
  }, [images]);

  return (
    <ProfilePictureContext.Provider
      value={{
        images,
        isLoading,
        refreshProfilePicture,
        getImageUrl,
      }}
    >
      {children}
    </ProfilePictureContext.Provider>
  );
};
